import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MinusCircle, CheckCircle2, Clock, User } from "lucide-react";
import { toast } from "sonner";

const cn = (...c) => c.filter(Boolean).join(" ");
const today = () => new Date().toISOString().slice(0, 10);

// Codebase convention:
//   contact.type === "customer"      → subcontractor (VAT-registered company)
//   contact.type === "subcontractor" → self-employed driver
// The /adjustments endpoint expects contact_type of "subcontractor" | "self_employed".
const adjustmentTypeFor = (contact) =>
  (contact?.type === "subcontractor" ? "self_employed" : "subcontractor");

const roleLabelFor = (contact) =>
  (contact?.type === "subcontractor" ? "Self-employed" : "Subcontractor");

// Quick-reason presets to seed the deduction description (mirrors the JobCharges dialog).
const LATE_REASONS = [
  "Late delivery",
  "Late arrival",
  "Not delivered on time",
  "No POD",
  "Missed drop",
];

// Presets for the client-side extras (waiting time, congestion, etc.).
const EXTRA_PRESETS = [
  "Waiting time",
  "Congestion charge",
  "ULEZ charge",
  "Toll charge",
  "Parking / permit",
  "Extra drop",
  "Return / redelivery",
  "Out of hours",
  "Weekend surcharge",
  "Fuel surcharge",
  "Handball / loading",
  "Storage fee",
  "Admin fee",
];

/**
 * Modal that runs the "confirm complete" workflow for a job:
 *   1) List every subcontractor / self-employed driver assigned to the job
 *      (top-level for standard, per-day for DSP — deduplicated).
 *   2) Ask "Was this person on time?" for each.
 *   3) If not, prompt for how many minutes late and offer to add a deduction
 *      straight into the charges/deductions system.
 *   4) On confirm: POST the deductions, then flip the job to `complete`.
 */
export default function CompleteJobDialog({ open, onOpenChange, job, onCompleted }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState({}); // contact_id -> { onTime, minutesLate, addDeduction, amount, description }
  // Client extra-charge state — one optional chargeable adjustment for the client.
  const [extra, setExtra] = useState({ add: false, amount: "", description: "" });

  // Pull all contacts once when the dialog opens so we can enrich names / types.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setHasLoaded(false);
    api.get("/contacts")
      .then((r) => { if (!cancelled) setContacts(r.data || []); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => { if (!cancelled) { setLoading(false); setHasLoaded(true); } });
    return () => { cancelled = true; };
  }, [open]);

  // Reset local answers whenever we open a different job.
  useEffect(() => {
    if (open) {
      setAnswers({});
      setExtra({ add: false, amount: "", description: "" });
    }
  }, [open, job?.id]);

  const contactsById = useMemo(
    () => Object.fromEntries((contacts || []).map((c) => [c.id, c])),
    [contacts],
  );

  // Build the deduped list of contacts that worked this job. For DSP jobs we
  // walk every dsp_day. Standard jobs just use the top-level subcontractor_id.
  const participants = useMemo(() => {
    if (!job) return [];
    const seen = new Map(); // contact_id -> { contact, days: Set<string> }
    const pushId = (id, day) => {
      if (!id) return;
      const c = contactsById[id];
      if (!c) return;
      const entry = seen.get(id) || { contact: c, days: new Set() };
      if (day) entry.days.add(day);
      seen.set(id, entry);
    };
    // Standard top-level
    pushId(job.subcontractor_id, job.date);
    // DSP per-day (top-level id is a fallback if a day doesn't set its own)
    for (const d of (job.dsp_days || [])) {
      pushId(d.subcontractor_id || job.subcontractor_id, d.date);
    }
    return Array.from(seen.values()).map(({ contact, days }) => ({
      contact,
      days: Array.from(days).sort(),
    }));
  }, [job, contactsById]);

  const setAns = (cid, patch) =>
    setAnswers((a) => ({ ...a, [cid]: { ...(a[cid] || {}), ...patch } }));

  // Prefill the deduction description with a helpful default once "add deduction" is toggled on.
  const defaultDescriptionFor = (cid) => {
    const a = answers[cid] || {};
    const mins = a.minutesLate;
    if (mins) return `Late delivery — ${mins} min${Number(mins) === 1 ? "" : "s"} late`;
    return "Late delivery";
  };

  const canConfirm = useMemo(() => {
    if (participants.length) {
      const ok = participants.every(({ contact, days }) => {
        const a = answers[contact.id];
        if (!a || a.onTime === undefined || a.onTime === null) return false;
        if (a.onTime === false && a.addDeduction) {
          const amt = Number(a.amount);
          if (!(amt > 0)) return false;
          // Multi-day jobs: at least one late day must be picked.
          if (days.length > 1 && (!Array.isArray(a.lateDays) || a.lateDays.length === 0)) return false;
        }
        return true;
      });
      if (!ok) return false;
    }
    if (extra.add) {
      const amt = Number(extra.amount);
      if (!(amt > 0)) return false;
    }
    return true;
  }, [participants, answers, extra]);

  const confirm = async () => {
    if (!job) return;
    setSaving(true);
    try {
      // Log any deductions the user opted into.
      const posts = [];
      for (const { contact, days } of participants) {
        const a = answers[contact.id];
        if (a?.onTime === false && a?.addDeduction) {
          const amount = Number(a.amount) || 0;
          if (amount <= 0) continue;
          const baseDesc = (a.description || "").trim() || defaultDescriptionFor(contact.id);
          // Multi-day DSP: create one deduction per late day. Each row is
          // dated on the specific day so reports slice correctly.
          const targetDays = (days.length > 1 && Array.isArray(a.lateDays) && a.lateDays.length)
            ? a.lateDays
            : [job.date || today()];
          for (const d of targetDays) {
            posts.push(api.post("/adjustments", {
              kind: "deduction",
              date: d,
              description: targetDays.length > 1 ? `${baseDesc} (${d})` : baseDesc,
              amount,
              job_id: job.id || null,
              client_id: job.client_id || null,
              contact_id: contact.id,
              contact_type: adjustmentTypeFor(contact),
              status: "pending",
            }));
          }
        }
      }
      // And log the optional client extra charge.
      if (extra.add) {
        const amount = Number(extra.amount) || 0;
        if (amount > 0) {
          posts.push(api.post("/adjustments", {
            kind: "charge",
            date: today(),
            description: (extra.description || "").trim() || "Extra charge",
            amount,
            job_id: job.id || null,
            client_id: job.client_id || null,
            status: "pending",
          }));
        }
      }
      if (posts.length) {
        const results = await Promise.allSettled(posts);
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed) toast.error(`${failed} adjustment(s) failed to save.`);
        else toast.success(`Logged ${results.length} entr${results.length === 1 ? "y" : "ies"} in Charges.`);
      }
      // Finally, flip the job to complete via the parent's callback.
      await onCompleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Even when no subs/drivers are on the job we still want to show the dialog
  // because ops may want to log a client extra charge. So no auto-skip.

  return (
    <Dialog open={open} onOpenChange={(v) => (saving ? null : onOpenChange(v))}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="complete-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Complete {job?.number} — confirm performance & extras
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading contacts…
          </div>
        )}

        {!loading && participants.length === 0 && (
          <div className="py-3 text-sm text-slate-600">
            No subcontractor or self-employed driver is assigned to this job.
          </div>
        )}

        {!loading && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {participants.length > 0 && (
              <p className="text-sm text-slate-600">
                For each person, tell us whether they hit their windows. If not, add a deduction — it'll be logged in Jobs → Charges automatically.
              </p>
            )}
            {participants.map(({ contact, days }) => {
              const a = answers[contact.id] || {};
              return (
                <div
                  key={contact.id}
                  data-testid={`complete-contact-${contact.id}`}
                  className="rounded-sm border border-slate-200 p-4 bg-slate-50/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="w-4 h-4 text-slate-500 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{contact.name}</div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-500">
                          {roleLabelFor(contact)}
                          {days.length > 0 && ` · ${days.length} day${days.length === 1 ? "" : "s"}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        data-testid={`ontime-yes-${contact.id}`}
                        onClick={() => setAns(contact.id, { onTime: true, minutesLate: "", addDeduction: false, amount: "", description: "" })}
                        className={cn(
                          "px-3 h-8 rounded-sm border text-xs font-semibold uppercase tracking-wider",
                          a.onTime === true
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50",
                        )}
                      >
                        On time
                      </button>
                      <button
                        type="button"
                        data-testid={`ontime-no-${contact.id}`}
                        onClick={() => setAns(contact.id, { onTime: false })}
                        className={cn(
                          "px-3 h-8 rounded-sm border text-xs font-semibold uppercase tracking-wider",
                          a.onTime === false
                            ? "bg-rose-600 text-white border-rose-600"
                            : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50",
                        )}
                      >
                        Late
                      </button>
                    </div>
                  </div>

                  {a.onTime === false && (
                    <div className="mt-3 rounded-sm border border-rose-200 bg-rose-50/40 p-3 space-y-3">
                      {days.length > 1 && (
                        <div>
                          <Label className="text-xs text-slate-600">Which days were they late?</Label>
                          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`late-days-${contact.id}`}>
                            {days.map((d) => {
                              const picked = Array.isArray(a.lateDays) && a.lateDays.includes(d);
                              return (
                                <button
                                  type="button"
                                  key={d}
                                  data-testid={`late-day-${contact.id}-${d}`}
                                  onClick={() => {
                                    const current = Array.isArray(a.lateDays) ? a.lateDays : [];
                                    const next = picked ? current.filter((x) => x !== d) : [...current, d];
                                    setAns(contact.id, { lateDays: next });
                                  }}
                                  className={cn(
                                    "px-2.5 h-7 rounded-sm border text-[11px] font-semibold tabular-nums transition",
                                    picked
                                      ? "bg-rose-600 text-white border-rose-600"
                                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                                  )}
                                >
                                  {d}
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            {Array.isArray(a.lateDays) && a.lateDays.length
                              ? `${a.lateDays.length} of ${days.length} day${days.length === 1 ? "" : "s"} selected`
                              : "Tick every day they were late — one deduction row will be logged per day."}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                          <Label className="text-xs text-slate-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> How many minutes late?
                          </Label>
                          <Input
                            data-testid={`minutes-late-${contact.id}`}
                            type="number"
                            min="0"
                            value={a.minutesLate || ""}
                            onChange={(e) => setAns(contact.id, { minutesLate: e.target.value })}
                            className="mt-1 rounded-sm border-slate-300 tabular-nums"
                            placeholder="e.g. 15"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            data-testid={`add-ded-yes-${contact.id}`}
                            onClick={() => setAns(contact.id, {
                              addDeduction: true,
                              description: a.description || defaultDescriptionFor(contact.id),
                            })}
                            className={cn(
                              "flex-1 h-9 rounded-sm border text-xs font-semibold uppercase tracking-wider",
                              a.addDeduction === true
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                            )}
                          >
                            <MinusCircle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                            Add deduction
                          </button>
                          <button
                            type="button"
                            data-testid={`add-ded-no-${contact.id}`}
                            onClick={() => setAns(contact.id, { addDeduction: false, amount: "", description: "" })}
                            className={cn(
                              "flex-1 h-9 rounded-sm border text-xs font-semibold uppercase tracking-wider",
                              a.addDeduction === false
                                ? "bg-slate-200 text-slate-800 border-slate-300"
                                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                            )}
                          >
                            No deduction
                          </button>
                        </div>
                      </div>

                      {a.addDeduction && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-600">
                              Amount (£{days.length > 1 ? ", per day" : ""})
                            </Label>
                            <Input
                              data-testid={`ded-amount-${contact.id}`}
                              type="number" step="0.01" min="0"
                              value={a.amount || ""}
                              onChange={(e) => setAns(contact.id, { amount: e.target.value })}
                              className="mt-1 rounded-sm border-slate-300 tabular-nums"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Quick reason</Label>
                            <Select
                              value=""
                              onValueChange={(v) => setAns(contact.id, { description: v })}
                            >
                              <SelectTrigger
                                data-testid={`ded-reason-${contact.id}`}
                                className="mt-1 rounded-sm border-slate-300 h-9 text-slate-600"
                              >
                                <SelectValue placeholder="Pick a reason…" />
                              </SelectTrigger>
                              <SelectContent>
                                {LATE_REASONS.map((r) => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs text-slate-600">Description</Label>
                            <Textarea
                              data-testid={`ded-description-${contact.id}`}
                              rows={2}
                              value={a.description || ""}
                              onChange={(e) => setAns(contact.id, { description: e.target.value })}
                              className="mt-1 rounded-sm border-slate-300"
                              placeholder={defaultDescriptionFor(contact.id)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ---- Client extra charge section ---- */}
            <div
              data-testid="complete-extra-block"
              className="rounded-sm border border-slate-200 p-4 bg-slate-50/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    Any extras to charge the client?
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">
                    {job?.customer_snapshot?.name || "Client"} · e.g. waiting time, congestion, admin
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    data-testid="extra-yes"
                    onClick={() => setExtra((x) => ({ ...x, add: true }))}
                    className={cn(
                      "px-3 h-8 rounded-sm border text-xs font-semibold uppercase tracking-wider",
                      extra.add === true
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50",
                    )}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    data-testid="extra-no"
                    onClick={() => setExtra({ add: false, amount: "", description: "" })}
                    className={cn(
                      "px-3 h-8 rounded-sm border text-xs font-semibold uppercase tracking-wider",
                      extra.add === false
                        ? "bg-slate-200 text-slate-800 border-slate-300"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50",
                    )}
                  >
                    No
                  </button>
                </div>
              </div>

              {extra.add && (
                <div className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50/40 p-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Amount (£)</Label>
                    <Input
                      data-testid="extra-amount"
                      type="number" step="0.01" min="0"
                      value={extra.amount || ""}
                      onChange={(e) => setExtra((x) => ({ ...x, amount: e.target.value }))}
                      className="mt-1 rounded-sm border-slate-300 tabular-nums"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Quick extra</Label>
                    <Select
                      value=""
                      onValueChange={(v) => setExtra((x) => ({ ...x, description: v }))}
                    >
                      <SelectTrigger
                        data-testid="extra-preset"
                        className="mt-1 rounded-sm border-slate-300 h-9 text-slate-600"
                      >
                        <SelectValue placeholder="Pick an extra…" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXTRA_PRESETS.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-600">Description</Label>
                    <Textarea
                      data-testid="extra-description"
                      rows={2}
                      value={extra.description || ""}
                      onChange={(e) => setExtra((x) => ({ ...x, description: e.target.value }))}
                      className="mt-1 rounded-sm border-slate-300"
                      placeholder="e.g. 30 mins waiting time at drop"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="rounded-sm">
            Cancel
          </Button>
          <Button
            data-testid="complete-confirm"
            onClick={confirm}
            disabled={!canConfirm || saving}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Completing…</>
            ) : (
              <>Confirm & complete job</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
